import { useEffect, useRef, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";

const GENERATE_PRESIGNED_DOWNLOAD = gql`
  mutation GeneratePresignedDownload($input: PresignedDownloadInput!) {
    result: generatePresignedDownload(input: $input) {
      url
      expiresAt
    }
  }
`;

// Refresh a bit before the signed URL actually expires, so a screen that
// stays open for a long time never shows a dead image in the gap between
// "still rendering the old URL" and "noticing it broke."
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

type Result = {
  url: string | null;
  loading: boolean;
  error: boolean;
};

/**
 * Fetches a signed, time-limited URL for a private image reference (an
 * objectReference returned from generatePresignedUpload, or a legacy public
 * URL -- either way, generatePresignedDownload knows how to resolve it) and
 * keeps it fresh for as long as the component stays mounted.
 */
export function usePresignedImageUrl(reference?: string | null): Result {
  const client = useApolloClient();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!reference) return undefined;

    const fetchUrl = async () => {
      try {
        const { data } = await client.mutate({
          mutation: GENERATE_PRESIGNED_DOWNLOAD,
          variables: { input: { objectReference: reference } },
        });
        if (cancelled) return;

        const result = data?.result;
        if (!result?.url) {
          setError(true);
          return;
        }
        setUrl(result.url);

        if (result.expiresAt) {
          const msUntilExpiry = new Date(result.expiresAt).getTime() - Date.now();
          const refreshIn = Math.max(msUntilExpiry - REFRESH_MARGIN_MS, MIN_REFRESH_DELAY_MS);
          timeoutRef.current = setTimeout(fetchUrl, refreshIn);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    fetchUrl();

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [reference, client]);

  return { url, loading: !url && !error && !!reference, error };
}
